<template>
  <div class="inline-flex items-center gap-2">
    <span v-if="!hideLabel" class="text-sm font-medium text-gray-600">Engagement Score:</span>
    <div class="inline-block px-1">
      <CC_Engagement_Score_Badge :params="{ value: compositeScoreForBadge }" />
    </div>
    <button
      class="text-emerald-600 hover:text-emerald-800 text-xs font-medium whitespace-nowrap disabled:text-gray-400 disabled:hover:text-gray-400 disabled:cursor-not-allowed"
      :disabled="isScoreButtonDisabled"
      :title="scoreButtonTooltip"
      @click="openModal"
    >
      {{ scoreButtonLabel }}
    </button>

    <!-- Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-20 py-10">
      <div class="w-full max-h-full overflow-auto bg-white rounded-lg shadow-md p-6">
        <h3 class="text-xl font-semibold text-gray-800 mb-4">Log Engagement Level</h3>
        <p class="text-sm text-gray-600 mb-4">
          Please consider engagement across your entire interaction, including during meetings,
          during EMR access conversations, and during other requests for information. Provide a score
          from 1 (poor engagement) to 5 (excellent engagement).
        </p>

        <!-- Score Selection -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Engagement Score:</label>
          <div class="flex gap-2">
            <button
              v-for="option in scoreOptions"
              :key="option.value"
              type="button"
              class="w-10 h-10 rounded-full flex items-center justify-center text-white transition-all"
              :class="getScoreButtonClass(option)"
              @click="score = score === option.value ? null : option.value"
            >
              {{ option.value }}
            </button>
          </div>
          <div v-if="score !== null" class="mt-2 text-sm text-gray-600">
            Selected: {{ score }}
          </div>
        </div>

        <!-- Comment -->
        <div class="mb-4">
          <label for="comments" class="block text-sm font-medium text-gray-700 mb-2">Comments:</label>
          <textarea
            id="comments"
            v-model="comment"
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
            placeholder="Optional comments..."
          ></textarea>
        </div>

        <!-- Previous Scores -->
        <div class="mb-4">
          <Generic_Table
            :column_defs="columnDefs"
            :row-data="engagement_scores"
            :auto-height="true"
            :table_button_text="'Previous Engagement Scores'"
          />
        </div>

        <!-- Actions -->
        <div class="flex gap-3">
          <button
            type="button"
            class="btn-primary"
            :disabled="score === null || loading"
            @click="saveScore"
          >
            {{ loading ? 'Saving...' : 'Save' }}
          </button>
          <button
            type="button"
            class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            @click="closeModal"
          >
            Cancel
          </button>
        </div>

        <div v-if="error" class="mt-4 text-red-600 text-sm">{{ error }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import Generic_Table from '@/components/Lists/Generic_Table.vue';
import CC_Engagement_Score_Badge from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Engagement_Score_Badge.vue';

export default {
  name: 'Detail_EntityEngagementLevel',
  components: {
    Generic_Table,
    CC_Engagement_Score_Badge
  },
  props: {
    entityId: {
      type: Number,
      required: true
    },
    hideLabel: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showModal: false,
      score: null,
      comment: '',
      loading: false,
      error: null,
      engagement_scores: [],
      compositeScoreData: null,
      scoreOptions: [
        { value: 1, bg: 'bg-red-700', ring: 'ring-red-300' },
        { value: 2, bg: 'bg-orange-700', ring: 'ring-orange-300' },
        { value: 3, bg: 'bg-yellow-600', ring: 'ring-yellow-300' },
        { value: 4, bg: 'bg-yellow-500', ring: 'ring-yellow-200' },
        { value: 5, bg: 'bg-green-700', ring: 'ring-green-300' }
      ],
      columnDefs: [
        {
          headerName: 'Date',
          field: 'created_at',
          sort: 'desc',
          width: 100,
          cellRenderer: params => new Date(params.value).toISOString().split('T')[0]
        },
        { headerName: 'By', field: 'appuser_name', width: 100 },
        { headerName: 'Score', field: 'engagement_score', width: 75 },
        { headerName: 'Comment', field: 'comment', filter: false, sortable: false }
      ]
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug', 'hasRole']),
    compositeScoreForBadge() {
      if (!this.compositeScoreData) return null;
      return {
        composite: this.compositeScoreData.composite_score,
        sub_scores: this.compositeScoreData.sub_scores || {}
      };
    },
    currentQuarterStart() {
      const now = new Date();
      return new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    },
    nextQuarterStart() {
      const q = this.currentQuarterStart;
      return new Date(q.getFullYear(), q.getMonth() + 3, 1);
    },
    nextQuarterStartDisplay() {
      return this.nextQuarterStart.toISOString().split('T')[0];
    },
    existingQuarterScore() {
      if (this.current_tenant_slug !== 'diverge') return null;
      if (!this.current_user?.id || !this.engagement_scores?.length) return null;
      const quarterStart = this.currentQuarterStart;
      return (
        this.engagement_scores.find(
          s => s.appuser_id === this.current_user.id && new Date(s.created_at) >= quarterStart
        ) || null
      );
    },
    currentQuarterLabel() {
      return `Q${Math.floor(new Date().getMonth() / 3) + 1}`;
    },
    scoreButtonLabel() {
      if (this.current_tenant_slug !== 'diverge') return '+ Add User Score';
      return this.existingQuarterScore
        ? `Edit ${this.currentQuarterLabel} Score`
        : `Add ${this.currentQuarterLabel} Score`;
    },
    isScoreButtonDisabled() {
      return (
        this.current_tenant_slug === 'diverge'
        && !!this.existingQuarterScore
        && !this.hasRole('central_practice_operations')
        && !this.current_user?.is_superuser
      );
    },
    scoreButtonTooltip() {
      return this.isScoreButtonDisabled ? `${this.currentQuarterLabel} score complete` : '';
    }
  },
  watch: {
    entityId: {
      immediate: true,
      handler() {
        this.fetchEngagementScores();
        this.fetchCompositeScore();
      }
    }
  },
  methods: {
    getScoreButtonClass(option) {
      if (this.score === option.value) {
        return `${option.bg} ring-4 ${option.ring} scale-110`;
      }
      return this.score ? `${option.bg} opacity-50` : `${option.bg} hover:opacity-80`;
    },
    async fetchCompositeScore() {
      try {
        const response = await axios.get(`/api/entity-engagement-scores/${this.entityId}/current/`);
        this.compositeScoreData = response.data;
      } catch (err) {
        this.compositeScoreData = null;
      }
    },
    async fetchEngagementScores() {
      try {
        const response = await axios.get('/api/engagementscores/', {
          params: { entity_id: this.entityId }
        });
        this.engagement_scores = response.data;
      } catch (err) {
        console.error('Error fetching engagement scores:', err);
      }
    },
    openModal() {
      const existing = this.existingQuarterScore;
      if (existing) {
        this.score = existing.engagement_score;
        this.comment = existing.comment || '';
      } else {
        this.score = null;
        this.comment = '';
      }
      this.error = null;
      this.showModal = true;
    },
    async saveScore() {
      if (this.score === null) return;

      this.loading = true;
      this.error = null;

      try {
        const existing = this.existingQuarterScore;
        const response = existing
          ? await axios.patch(`/api/engagementscores/${existing.id}/`, {
              engagement_score: this.score,
              comment: this.comment
            })
          : await axios.post('/api/engagementscores/', {
              appuser: this.current_user.id,
              engagement_score: this.score,
              comment: this.comment,
              entity_id: this.entityId
            });
        await Promise.all([
          this.fetchEngagementScores(),
          this.fetchCompositeScore()
        ]);
        this.$emit('saved', response.data);
        this.closeModal();
      } catch (err) {
        this.error = 'Failed to save engagement score. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    closeModal() {
      this.showModal = false;
      this.score = null;
      this.comment = '';
      this.error = null;
    }
  }
};
</script>
